import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, Inject, Input, OnInit, Output, EventEmitter, ChangeDetectionStrategy, NgZone, OnDestroy } from '@angular/core';
import { DeviceService } from '@shared/utils/services';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil, throttleTime } from 'rxjs/operators';


@Component({
    selector: 'shared-component-sticky-banner',
    templateUrl: './sticky-banner.component.html',
    styleUrls: ['./sticky-banner.component.scss'],
    imports: [
        CommonModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StickyBannerComponent implements OnInit, OnDestroy {

	/**
	 * @description
	 * Represents current visual state of the sticky banner
	 */
	public isBannerShowing = false;

	/**
	 * @description
	 * Used to store styling position info after isMobile is determined
	 */
	private position: string;

	/**
	 * @description
	 * Flag set to true upon first showing of banner. Used to avoid emitAnalyticsEvent from emitting more than once
	 */
	private hasEmittedAnalyticsEvent = false;

	/**
	 * @description
	 * Object used to hold styling information for the ngClass directive used in the template
	 */
	public bannerStyle: any = {};

	/**
	 * @description
	 * Boolean representing if user is on a mobile device.
	 */
	public isMobile: boolean;

	/**
	 * @description
	 * The selected HTML element that will be used for calculations
	 */
	private selectedElement: HTMLElement;

	/**
	 * @description
	 * String representing whether to display the sticky banner on 'top' or 'bottom' of a mobile page
	 */
	@Input()
	public mobilePosition = 'bottom';

	/**
	 * @description
	 * String representing whether to display the sticky banner on 'top' or 'bottom' of a non-mobile page
	 */
	@Input()
	public desktopPosition = 'top';

	/**
	 * @description
	 * If the sticky banner is intended to only show on mobile size viewports
	 */
	@Input()
	public mobileOnly = false;

	/**
	 * @description
	 * String used to select the element to calculate when to show the sticky banner.
	 */
	@Input()
	public elementToCalculateOffset: string;

	/**
	 * @description Number transition duration in milliseconds.
	 */
	@Input()
	public duration = 200;

	/**
	 * @description Desktop height of sticky banner in px.
	 */
	@Input()
	public desktopHeight = 72;

	/**
	 * @description mobile height of sticky banner in px.
	 */
	@Input()
	public mobileHeight = 52;

	/**
	 * @description Outputs an event emitter each time the banner is show
	 */
	@Output()
	public afterShowBanner = new EventEmitter();

	/**
	 * @description Outputs an event emitter for analytics.
	 * This will only fire the first time the banner is shown
	 */
	@Output()
	public emitAnalyticsEvent = new EventEmitter();

	/**
	 * @description Outputs an event emitter each time the banner is hidden
	 */
	@Output()
	public afterHideBanner = new EventEmitter();

	/**
	 * @description Subject that is updated when window is scrolled which then triggers handleScroll function
	 */

	public destroy$ = new Subject<void>();
	constructor(@Inject(DOCUMENT) private document: any, private deviceService: DeviceService, private ngZone: NgZone) {
	}


	ngOnInit() {
		// determine if the viewport is mobile
		this.isMobile = this.deviceService.isMobileBreakpoint();
		this.position = this.isMobile ? this.mobilePosition : this.desktopPosition;

		// determine the appearance of the banner
		this.getBannerStyle();

		const throttleTimeValue = 100;
		// Scroll logic is throttled to run less frequently in order to improve performance
		this.ngZone.runOutsideAngular(() => {
			fromEvent(window, 'scroll')
			  // Unsubscribe from the scrollSubject when the component is destroyed.
			  .pipe(
					takeUntil(this.destroy$), // Take until the scrollSubject emits a value
					throttleTime(throttleTimeValue) // Throttle scroll event handling to a 100ms interval
			  )
			  .subscribe(() => {
				// only and athlete device is mobile OR sticky banner is intended for all devices
					if ((this.mobileOnly && this.isMobile) || !this.mobileOnly) {
						// Ensure changes in handleScroll trigger Angular's change detection.
						// Only perform scroll logic if sticky banner is mobile
						this.ngZone.run(() => {
							this.handleScroll();
						});
					}
			  });
		  });
	}

	ngOnDestroy() {
		// Notify that no more values will be emitted by destroy$
		this.destroy$.next();
		// Close the destroy$
		this.destroy$.complete();
	}

	/**
	 * @description Checks the current viewport to decide if the sticky banner should be displayed
	 */
	public handleScroll() {
		// eslint-disable-next-line prefer-const
		let boundingRect: DOMRect;

		if(!this.selectedElement || !boundingRect?.height){
			// Selecting element here, in case element is not rendered yet. Will also reselect the element if previous selection is no longer valid.
			this.selectedElement = this.document.querySelector(this.elementToCalculateOffset);
		}
		// Returned value is a DOMRect object which is the smallest rectangle which contains the entire element
		boundingRect = this.selectedElement?.getBoundingClientRect();

		// Used to determine if top of viewport has scrolled beyond the select element
		// Checking the current state of the banner via this.isBannerShowing flag ensures the showBanner() function isn't called excessively during scrolling event
		if(boundingRect?.bottom < 0 && !this.isBannerShowing){
			this.showBanner();
		}

		// Checking the current state of the banner via this.isBannerShowing flag ensures the  hideBanner function isn't called excessively during scrolling event
		if(boundingRect?.bottom > 0 && this.isBannerShowing){
			this.hideBanner();
		}
	}

	/**
	 * @description Returns an object with the height, slide position, and duration for the sticky banner.
	 * Banner will be at the bottom on mobile and on the top for desktop.
	 */
	public getBannerStyle(): any {
		this.bannerStyle.height = `${this.isMobile ? this.mobileHeight : this.desktopHeight}px`;

		this.bannerStyle.transition = `${this.position} ${this.duration}ms, visibility ${this.duration}ms`;

		// sets a value for css bottom or top property
		// banner set to hidden position by default
		this.bannerStyle[this.position] = `-${this.bannerStyle.height}`;

	}

	public hideBanner(){
		this.isBannerShowing = false;
		this.bannerStyle[this.position]	= `-${this.bannerStyle.height}`;
		this.afterHideBanner.emit();
	}

	public showBanner(){

		this.isBannerShowing = true;
		this.bannerStyle[this.position]	= 0;
		this.afterShowBanner.emit();

		// emit analytics event when the banner is shown for the first time
		if(!this.hasEmittedAnalyticsEvent){
			this.hasEmittedAnalyticsEvent = true;
			this.emitAnalyticsEvent.emit();
		}
	}
}
